import React, { useEffect, type JSX } from "react";

import { useAppDispatch } from "./hooks/reduxHooks";
import { setFullScreen } from "./DocumentPreviewSlice_Store";
import {
  type DocumentData,
  filenameOf,
  isGeneratedDraftFile,
} from "./components/documentPreview/documentPreviewTypes";
import { useDocumentPreview } from "./components/documentPreview/useDocumentPreview";
import { useDocumentFacts } from "./components/documentPreview/useDocumentFacts";
import { useDraftEditing } from "./components/documentPreview/useDraftEditing";
import { useDocumentActions } from "./components/documentPreview/useDocumentActions";
import DraftHeaderBar from "./components/documentPreview/DraftHeaderBar";
import PreviewBody from "./components/documentPreview/PreviewBody";
import {
  NoDocumentSelected,
  PreviewNotAvailable,
  SaveErrorBanner,
} from "./components/documentPreview/PreviewStates";

export interface DocumentPreviewTabProps {
  document: DocumentData | null;
  onClose: () => void;
}

/**
 * Previews a single document: PDFs with citation highlighting, drafts with
 * inline editing, and everything else as rendered text.
 */
export default function DocumentPreviewTab_Component({
  document,
}: DocumentPreviewTabProps): JSX.Element {
  const dispatch = useAppDispatch();

  // Order matters: the preview effects must settle before the citation ones run
  const preview = useDocumentPreview(document);
  const facts = useDocumentFacts(document, preview.loading);
  const draft = useDraftEditing(document, preview);
  const actions = useDocumentActions(document, preview);

  // Full screen is a view mode, not document state - never outlive the tab
  useEffect(() => {
    return () => {
      dispatch(setFullScreen(false));
    };
  }, [dispatch]);

  if (!document) {
    return <NoDocumentSelected />;
  }

  if (!preview.canPreview) {
    return <PreviewNotAvailable filename={filenameOf(document)} />;
  }

  return (
    <div className="flex flex-col h-full bg-cvc-canvas">
      {document.documentCategory === "draft" && (
        <DraftHeaderBar
          document={document}
          draft={draft}
          isGeneratedFile={isGeneratedDraftFile(document)}
          documentType={preview.documentType}
          loading={preview.loading}
          onRefresh={() => void preview.reload()}
        />
      )}

      {draft.saveError && <SaveErrorBanner message={draft.saveError} />}

      <div className="flex-1 flex flex-col overflow-hidden">
        <PreviewBody
          document={document}
          preview={preview}
          facts={facts}
          draft={draft}
          actions={actions}
        />
      </div>
    </div>
  );
}
